import { Play } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { HorseVideoIntro } from "@/components/media/HorseVideoIntro";
import { SmartVideo } from "@/components/media/SmartVideo";

/*
 * Video-first stage: large main player + categorized clip carousel.
 * videos: [{ id, url, category, label? }] — label marks bloodline footage (sire/dam name).
 * intro (Fase D): { horse, logoUrl, style } — rótulo de intro sobre el poster,
 * fade elegante al reproducir; el archivo de vídeo nunca se toca.
 */
export const VideoStage = ({ videos, poster, testid = "horse-video-stage", filter, onFilterChange, intro }) => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(videos[0]?.id);
  const [internalCategory, setInternalCategory] = useState("all");
  const category = filter ?? internalCategory;
  const setCategory = onFilterChange ?? setInternalCategory;

  const categories = useMemo(() => {
    const present = [...new Set(videos.map((v) => v.category || "other"))];
    const bloodlines = [...new Set(videos.map((v) => v.bloodline).filter(Boolean))];
    const chips = [...present, ...bloodlines.filter((b) => !present.includes(b))];
    return chips.length > 1 ? ["all", ...chips] : [];
  }, [videos]);

  const matches = (v, c) => c === "all" || (v.category || "other") === c || v.bloodline === c;
  const visible = useMemo(() => videos.filter((v) => matches(v, category)), [videos, category]);
  const active = videos.find((v) => v.id === activeId) || visible[0] || videos[0];

  // Fase D — ciclo del rótulo: visible sobre el poster, fade de ~2.5s al dar
  // play y NUNCA reaparece durante la reproducción de ese clip.
  const [introFading, setIntroFading] = useState(false);
  const [introHidden, setIntroHidden] = useState(false);
  const fadeTimer = useRef(null);
  useEffect(() => {
    setIntroFading(false);
    setIntroHidden(false);
    return () => clearTimeout(fadeTimer.current);
  }, [active?.id]);
  const dismissIntro = () => {
    setIntroFading((f) => {
      if (!f) fadeTimer.current = setTimeout(() => setIntroHidden(true), 2600);
      return true;
    });
  };
  const isEmbed = active?.playback?.type === "iframe" || active?.playback?.type === "embed";
  const showIntro = Boolean(intro?.horse) && !introHidden;

  if (!videos.length) return null;

  return (
    <section className="flex flex-col gap-5" data-testid={testid}>
      <figure className="relative rounded-3xl overflow-hidden bg-[#0A0A0A] shadow-[0_16px_40px_rgb(0,0,0,0.10)]">
        <SmartVideo
          key={active.id}
          playback={active.playback}
          src={active.url}
          poster={active.poster || poster || undefined}
          controls
          playsInline
          preload="metadata"
          onPlay={dismissIntro}
          iframeTitle={active.label || t(`vidcat.${active.category || "other"}`)}
          data-testid="video-main-player"
          className="w-full aspect-video object-contain bg-[#0A0A0A]"
        />
        {showIntro && (
          <HorseVideoIntro
            horse={intro.horse}
            logoUrl={intro.logoUrl}
            style={intro.style}
            fading={introFading}
            onTap={isEmbed && !introFading ? dismissIntro : undefined}
          />
        )}
        <div className="absolute top-4 left-4 flex items-center gap-2 pointer-events-none">
          <span className="px-3 h-7 inline-flex items-center rounded-full bg-black/55 backdrop-blur-md text-white text-xs font-medium">
            {t(`vidcat.${active.category || "other"}`)}
          </span>
          {active.label && (
            <span
              className="px-3 h-7 inline-flex items-center rounded-full bg-white/85 backdrop-blur-md text-[#0A0A0A] text-xs font-semibold"
              data-testid="video-bloodline-label"
            >
              {active.label}
            </span>
          )}
        </div>
      </figure>

      {categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label={t("pub.videos")}>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={category === c}
              data-testid={`video-category-${c}`}
              onClick={() => setCategory(c)}
              className={`px-4 h-8 rounded-full text-xs font-medium transition-colors duration-200 ${
                category === c ? "bg-[#0A0A0A] text-white" : "bg-black/5 text-[#0A0A0A]/60 hover:text-[#0A0A0A]"
              }`}
            >
              {c === "all" ? t("vidcat.all") : t(`vidcat.${c}`)}
            </button>
          ))}
        </div>
      )}

      {videos.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x" data-testid="video-carousel">
          {visible.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setActiveId(v.id)}
              data-testid={`video-thumb-${v.id}`}
              aria-label={`${t(`vidcat.${v.category || "other"}`)}${v.label ? ` — ${v.label}` : ""}`}
              className={`relative shrink-0 w-44 sm:w-52 rounded-2xl overflow-hidden snap-start group transition-all duration-200 ${
                active.id === v.id ? "ring-2 ring-[#0A0A0A] ring-offset-2" : "opacity-80 hover:opacity-100"
              }`}
            >
              {!v.playback || v.playback.type === "file" ? (
                <video src={v.url} preload="metadata" muted playsInline tabIndex={-1} className="w-full aspect-video object-cover bg-[#111] pointer-events-none" />
              ) : v.poster ? (
                <img src={v.poster} alt="" className="w-full aspect-video object-cover bg-[#111] pointer-events-none" />
              ) : (
                <span className="block w-full aspect-video bg-[#111]" aria-hidden="true" />
              )}
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <Play className="w-4 h-4 text-white fill-white ml-0.5" aria-hidden="true" />
                </span>
              </span>
              <span className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5">
                <span className="px-2 h-5 inline-flex items-center rounded-full bg-black/60 text-white text-[10px] font-medium">
                  {t(`vidcat.${v.category || "other"}`)}
                </span>
                {v.label && (
                  <span className="px-2 h-5 inline-flex items-center rounded-full bg-white/85 text-[#0A0A0A] text-[10px] font-semibold truncate">
                    {v.label}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
};
